<template>
  <div class="chart-wrap">
    <!-- 年份选择器 -->
    <van-button
      v-if="showYearSelect"
      round
      plain
      icon="arrow-down"
      icon-position="right"
      size="mini"
      color="#252525"
      @click="showPicker = true">{{ currentYear }}</van-button>
    <van-popup v-model="showPicker" position="bottom">
      <van-picker
        title="选择年份"
        show-toolbar
        :default-index="defaultYearIndex"
        :columns="yearList"
        @cancel="showPicker = false"
        @confirm="handleConfirm"
      />
    </van-popup>
    <!-- 图表 -->
    <div class="chart" ref="chart" style="height:315px"></div>
    <!-- 自定义图例 -->
    <chart-legend :style="legendStyle" :chartData="chartData" :legendCols="legendCols" :showBtn="showBtn" />
  </div>
</template>

<script>
import {
  Button,
  Popup,
  Picker
} from 'vant'
import * as echarts from 'echarts'
import resize from '@/common/mixins/resize'
import ChartLegend from '@/components/analyse/chart-legend'
import { colors, currentYear, defaultYearIndex } from '@/common/config'

export default {
  mixins: [resize],
  components: {
    [Button.name]: Button,
    [Popup.name]: Popup,
    [Picker.name]: Picker,
    ChartLegend
  },
  props: {
    title: {
      type: String,
      default: ''
    },
    legendCols: {
      type: [Number, String],
      default: 2
    },
    legendStyle: {
      type: Object,
      default() {
        return {}
      }
    },
    showBtn: {
      type: Boolean,
      default: false
    },
    chartData: {
      type: Array,
      default() {
        return []
      }
    },
    chartX: {
      type: Array,
      default() {
        return []
      }
    },
    eventName: {
      type: String,
      default: ''
    },
    showYearSelect: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      chart: null,
      option: {},
      showPicker: false,
      currentYear: currentYear,
      defaultYearIndex: defaultYearIndex
    }
  },
  computed: {
    yearList() {
      const date = new Date()
      let year = date.getFullYear()
      const years = []
      for(let i=0; i<10; i++) {
        years.push(year)
        year--
      }
      return years
    }
  },
  watch: {
    chartData() {
      this.setData()
      // 隐藏loading
      this.chart.hideLoading()
    }
  },
  mounted() {
    this.initChart()
  },
  methods: {
    // 选择年份
    handleConfirm(year) {
      this.currentYear = year
      this.showPicker = false
      this.$emit('handleYearChange', {eventName: this.eventName, year})
    },
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
      // 显示loading
      if(!this.chartData.length) {
        this.chart.showLoading({
          text: '',
          color: '#4292F5'
        })
      }
      this.setData()
    },
    setData() {
      const series = this.chartData.map((item, index) => {
        let itemStyle = {}
        if(index === 0) {
          itemStyle = {
            barBorderRadius: [5, 0, 0, 5]
          }
        }
        if(index === 1) {
          itemStyle = {
            barBorderRadius: [0, 5, 5, 0]
          }
        }
        return {
          name: item.name,
          type: 'bar',
          xAxisIndex: index,
          yAxisIndex: index,
          // stack: 'number',
          data: item.data,
          barWidth: 7,
          itemStyle
        }
      })
      const totalArr = []
      this.chartData.forEach(item => {
        item.data.forEach((iten, ind) => {
          totalArr[ind] = (totalArr[ind] || 0) + Number(iten)
        })
      })
      this.option = {
        color: colors,
        title: {
          text: this.title,
          top: 16,
          left: 10,
          textStyle: {
            fontSize: 16
          }
        },
        legend: { show: false }, // 必须配置，否则无法触发legend事件
        grid: [
          {
            left: 5,
            right: '45%',
            bottom: 20,
            containLabel: true,
          },
          {
            right: 15,
            left: '55%',
            bottom: 20,
            containLabel: true,
          },
        ],
        tooltip: {
          confine: true,
          trigger: 'axis',
          triggerOn: 'click',
          axisPointer: {
            type: 'none'
          },
          backgroundColor: '#fff',
          textStyle: { color: '#222' },
          extraCssText: 'box-shadow: 0px 10px 20px 0px rgba(0, 0, 0, 0.1);',
          formatter: (params) => {
            if(Array.isArray(params)) {
              const dataIndex = params[0].dataIndex
              const name = params[0].name
              let str = `${name}<br/>`
              this.chartData.forEach((item, index) => {
                str += `
                  <span style="display:inline-block;margin-right:5px;border-radius:10px;width:6px;height:6px;background-color:${colors[index]};"></span>
                  ${item.name}：${item.data[dataIndex]} &nbsp;${(item.data[dataIndex] / totalArr[dataIndex] * 100).toFixed(2)}%
                  <br/>
                `
              })
              return str
            }else {
              return ''
            }
          }
        },
        yAxis: [
          {
            axisLabel: {
              color: '#3B4058',
            },
            gridIndex: 0,
          },
          {
            gridIndex: 1,
            axisLabel: { show: false }
          },
        ].map(item => {
          return {
            type: 'category',
            gridIndex: item.gridIndex,
            axisLabel: item.axisLabel,
            data: this.chartX,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#efefef'
              }
            }
          }
        }),
        xAxis: [
          {
            inverse: true,  // 反转x轴
            gridIndex: 0,
          },
          {
            gridIndex: 1
          }
        ].map(item => {
          return {
            type: 'value',
            gridIndex: item.gridIndex,
            inverse: item.inverse,
            scale: true,
            axisLine: { show: false },
            axisTick: { show: false },
            splitNumber: 3,
            // interval: 2,
            axisLabel: {
              color: '#3B4058',
              formatter: (params) => {
                if(params > 0) {
                  return (params / 10000).toFixed(0) + '万'
                }else {
                  return params
                }
              }
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#efefef'
              }
            }
          }
        }),
        series: [
          ...series,
          // {
          //   name: '总数',
          //   type: 'line',
          //   // stack: 'number',
          //   data: seriesLine,
          //   symbolSize: 8,
          //   symbol: 'circle',
          //   itemStyle: {
          //     color: '#F93732',
          //     borderWidth: 2,
          //     borderColor: '#fff'
          //   }
          // },
        ]
      }
      this.chart.setOption(this.option)
    }
  }
}
</script>

<style lang="scss" scoped>
.van-button {
  position: absolute;
  top: 16PX;
  right: 10PX;
  z-index: 1;
  border-color: #DFE5EE !important;
}
</style>
